/**
 * « À VENIR » — annoncer une capacité NON CONSTRUITE, sans jamais l'offrir.
 *
 * ⚠️ CE N'EST PAS UN CONTRÔLE DÉSACTIVÉ, ET LA NUANCE EST TOUT. Un bouton grisé dit « vous n'y avez
 * pas droit » ou « pas maintenant » : l'opérateur cherche la permission ou la condition manquante, et
 * ne trouve rien. « À venir » dit « ça n'existe pas encore », ce qui n'appelle aucune démarche — la
 * même distinction que l'écran Intégrations tient déjà entre « déconnecté » et « à venir ».
 *
 * ⚠️ POURQUOI ON L'AFFICHE PLUTÔT QUE DE LE CACHER. §23.0 interdit l'option offerte non implémentée,
 * pas l'annonce d'une suite. Masquer ferait croire que le module est complet — et à la première
 * question (« et le double comptage ? »), personne ne saurait dire si c'est prévu, oublié, ou refusé.
 * Ce qui reste INTERDIT, c'est de rendre ça cliquable : rien ici n'est un `<button>`.
 *
 * ⚠️ ET SURTOUT, IL DIT CE QUE ÇA FERA. Un badge nu (« à venir ») est un aveu sans information ; la
 * phrase qui l'accompagne est ce qui permet de juger si l'absence est gênante aujourd'hui.
 */
import type { ReactNode } from 'react';
import { t } from '../i18n';

/** La pastille seule — pour coller à un titre de section ou de colonne. */
export function BadgeAVenir({ titre }: { titre?: string }) {
  return (
    <span
      title={titre}
      style={{
        display: 'inline-flex', alignItems: 'center', height: 19, padding: '0 8px', flexShrink: 0,
        borderRadius: 'var(--r-xs)', border: '1px dashed var(--ink-300)',
        background: 'transparent', color: 'var(--ink-500)',
        fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      
      {t('À venir')}
    </span>
  );
}

/**
 * Le bloc complet : ce que la capacité fera, et pourquoi son absence se remarque.
 *
 * ⚠️ EN POINTILLÉ ET SANS FOND, délibérément : une carte pleine se lit comme une fonction disponible.
 * Le trait discontinu dit « emplacement réservé » avant même qu'on lise le badge.
 */
export function BlocAVenir({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <div style={{
      display: 'flex', gap: 11, alignItems: 'flex-start', padding: '12px 14px',
      border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-sm)', background: 'transparent',
    }}>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-600)' }}>{titre}</span>
          <BadgeAVenir />
        </span>
        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          {children}
        </span>
      </span>
    </div>
  );
}
